import React, { useState } from 'react';
import { 
  Save, 
  Settings,
  Globe,
  Phone,
  Mail,
  MapPin,
  Palette
} from 'lucide-react';
import { useCMS } from '../../store/cmsStore';
import type { SiteSettings } from '../../store/cmsStore';

export default function SettingsEditor() {
  const { data, updateSettings } = useCMS();
  const [formData, setFormData] = useState<SiteSettings>(data.settings);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    updateSettings(formData);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleChange = (field: keyof SiteSettings, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 font-amiri">إعدادات الموقع</h2>
        <button
          onClick={handleSave}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-cairo ${
            saved
              ? 'bg-green-500 text-white'
              : 'bg-brown-600 text-white hover:bg-brown-700'
          }`}
        >
          <Save className="w-4 h-4" />
          {saved ? 'تم الحفظ!' : 'حفظ الإعدادات'}
        </button>
      </div>

      {/* General Settings */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center gap-3 mb-6">
          <Globe className="w-5 h-5 text-brown-600" />
          <h3 className="text-lg font-bold text-gray-900 font-cairo">معلومات الموقع</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
              اسم الموقع (عربي)
            </label>
            <input
              type="text"
              value={formData.siteName}
              onChange={(e) => handleChange('siteName', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
              اسم الموقع (إنجليزي)
            </label>
            <input
              type="text"
              value={formData.siteNameEn}
              onChange={(e) => handleChange('siteNameEn', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
            />
          </div>
        </div>
      </div>

      {/* Contact Settings */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center gap-3 mb-6">
          <Phone className="w-5 h-5 text-brown-600" />
          <h3 className="text-lg font-bold text-gray-900 font-cairo">معلومات التواصل</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
              رقم الهاتف
            </label>
            <input
              type="text"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
              placeholder="0560856311"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
              رقم واتساب
            </label>
            <input
              type="text"
              value={formData.whatsapp}
              onChange={(e) => handleChange('whatsapp', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
              placeholder="966575179602"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
              البريد الإلكتروني
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
              placeholder="info@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
              المدينة
            </label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
              العنوان الكامل
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
            />
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center gap-3 mb-6">
          <Settings className="w-5 h-5 text-brown-600" />
          <h3 className="text-lg font-bold text-gray-900 font-cairo">القسم الرئيسي (Hero)</h3>
        </div>

        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
              العنوان الرئيسي
            </label>
            <input
              type="text"
              value={formData.heroTitle}
              onChange={(e) => handleChange('heroTitle', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
              العنوان الفرعي
            </label>
            <textarea
              value={formData.heroSubtitle}
              onChange={(e) => handleChange('heroSubtitle', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
              rows={2}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
              نص الزر الرئيسي
            </label>
            <input
              type="text"
              value={formData.heroCta}
              onChange={(e) => handleChange('heroCta', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
            />
          </div>
        </div>
      </div>

      {/* About Section */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center gap-3 mb-6">
          <MapPin className="w-5 h-5 text-brown-600" />
          <h3 className="text-lg font-bold text-gray-900 font-cairo">قسم من نحن</h3>
        </div>

        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
              عنوان قسم من نحن
            </label>
            <input
              type="text"
              value={formData.aboutTitle}
              onChange={(e) => handleChange('aboutTitle', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
              وصف الشركة
            </label>
            <textarea
              value={formData.aboutDescription}
              onChange={(e) => handleChange('aboutDescription', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Colors */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center gap-3 mb-6">
          <Palette className="w-5 h-5 text-brown-600" />
          <h3 className="text-lg font-bold text-gray-900 font-cairo">الألوان</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
              اللون الأساسي
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={formData.primaryColor}
                onChange={(e) => handleChange('primaryColor', e.target.value)}
                className="w-12 h-12 rounded-lg border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={formData.primaryColor}
                onChange={(e) => handleChange('primaryColor', e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-mono"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
              اللون الثانوي
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={formData.secondaryColor}
                onChange={(e) => handleChange('secondaryColor', e.target.value)}
                className="w-12 h-12 rounded-lg border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={formData.secondaryColor}
                onChange={(e) => handleChange('secondaryColor', e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-mono"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save Button (Bottom) */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg transition font-cairo text-lg ${
            saved
              ? 'bg-green-500 text-white'
              : 'bg-brown-600 text-white hover:bg-brown-700'
          }`}
        >
          <Save className="w-5 h-5" />
          {saved ? 'تم الحفظ بنجاح!' : 'حفظ جميع الإعدادات'}
        </button>
      </div>
    </div>
  );
}